'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { useState } from 'react'

import { withAfSub3 } from './config'
import { FIRE_IMPRESSIONS_TIMEOUT } from './constants'

import type { IOneLinkConfig } from './types'

/** generateLink 호출 시점에 흐름별로 주입하는 동적 파라미터 */
export interface IGenerateOneLinkOverrides {
  /** af_sub3에 주입할 동적 값 (예: 정비소/차종 ID). 데이터 집계용. */
  afSub3?: string
}

export const useOneLink = (config: IOneLinkConfig) => {
  const [oneLinkUrl, setOneLinkUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  // SmartScript 동적 생성 실패 시에도 해당 흐름의 base OneLink(seo-geo/kFy7)를 사용
  const fallbackUrl = config.oneLinkURL

  const isAppsFlyerReady = () => {
    return typeof window !== 'undefined' && window.AF_SMART_SCRIPT
  }

  const generateLink = async (overrides?: IGenerateOneLinkOverrides) => {
    if (!isAppsFlyerReady()) {
      const error = new Error('AppsFlyer 스크립트 로드 실패')
      setError(error)
      return null
    }

    setIsLoading(true)
    setError(null)

    // af_sub3 동적 주입은 config 쪽 withAfSub3에 위임 (nested 구조 결합 제거)
    const resolvedConfig = overrides?.afSub3
      ? withAfSub3(config, overrides.afSub3)
      : config

    try {
      const result = window.AF_SMART_SCRIPT!.generateOneLinkURL(resolvedConfig)
      const url = result?.clickURL || fallbackUrl
      setOneLinkUrl(url)
      return url
    } catch (err) {
      setOneLinkUrl(fallbackUrl)
      const error = err as Error
      setError(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * 클릭 이벤트를 AppsFlyer에 전송하는 함수
   */
  const reportClickToAppsFlyer = () => {
    if (isAppsFlyerReady() && oneLinkUrl) {
      // 1초 지연 후 impression 발생 (AppsFlyer 권장사항)
      setTimeout(() => {
        window.AF_SMART_SCRIPT!.fireImpressionsLink()
      }, FIRE_IMPRESSIONS_TIMEOUT)
    }
  }

  const handleOneLinkClick = async (event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault()
    }

    // 클릭 시 캐시 우선, 없으면 생성
    const url = oneLinkUrl || (await generateLink())

    if (url) {
      // 1. OneLink URL로 이동
      window.open(url, '_blank')

      // 2. Google Ads Conversion 이벤트 발송 (development 환경에서는 제외)
      if (process.env.NEXT_PUBLIC_APP_ENV !== 'development') {
        const sendTo = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_SEND_TO_PATH
        if (!sendTo) {
          const error = new Error(
            'Google Tag Manager send_to path is not set. Skipping GTM event.'
          )
          setError(error)
          return
        }
        sendGTMEvent({
          event: 'conversion',
          send_to: sendTo,
        })
      }

      reportClickToAppsFlyer()
    } else {
      const error = new Error('OneLink URL 생성 실패')
      setError(error)
    }
  }

  return {
    error,
    isLoading,
    generateLink,
    handleOneLinkClick,
  }
}
